import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { api, inr, formatErr } from "@/lib/api";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import AllergenBadge from "@/components/AllergenBadge";
import { LogOut, Store, ShoppingBag, TrendingUp, Trophy, Banknote, ChefHat, Plus, Edit, Trash2, X, Upload, Sparkles, Loader2, Download } from "lucide-react";

const TAGS = ["veg", "vegan", "gf", "nuts", "dairy", "spicy", "egg"];

const empty = {
  name: "", category: "Snacks", price: 50, cost_price: 0,
  description: "", image_url: "", dietary_tags: [], allergens: [],
  available: true, stock_today: 100, is_junk_food: false,
  tax_rate: 5.0, is_mrp: false, hsn_code: "", nutrition: null,
};

const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-900",
  preparing: "bg-blue-100 text-blue-900",
  ready: "bg-emerald-100 text-emerald-900",
  completed: "bg-cream-300 text-ink-700",
};

export default function VendorPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [menu, setMenu] = useState([]);
  const [tab, setTab] = useState("dashboard");
  const [editing, setEditing] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [autoImg, setAutoImg] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user?.role !== "vendor") return;
    const load = () => {
      api.get("/vendors/me/dashboard").then(r => setData(r.data)).catch(()=>{});
      api.get("/vendors/me/orders").then(r => setOrders(r.data)).catch(()=>{});
      api.get("/vendors/me/menu").then(r => setMenu(r.data)).catch(()=>{});
    };
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [user]);

  const reloadMenu = () => api.get("/vendors/me/menu").then(r => setMenu(r.data)).catch(()=>{});

  const update = async (id, status) => {
    try { await api.patch(`/orders/${id}/status`, { status }); api.get("/vendors/me/orders").then(r => setOrders(r.data)); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const saveItem = async () => {
    try {
      const payload = {
        ...editing,
        price: Number(editing.price),
        cost_price: Number(editing.cost_price || 0),
        stock_today: Number(editing.stock_today || 0),
        tax_rate: Number(editing.tax_rate || 0),
      };
      if (editing.id) await api.put(`/menu/${editing.id}`, payload);
      else await api.post("/menu", payload);
      toast.success(editing.id ? "Updated" : "Created");
      setEditing(null); reloadMenu();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/menu/${id}`); toast.success("Deleted"); reloadMenu(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const toggleSoldOut = async (it) => {
    try { await api.patch(`/menu/${it.id}/sold-out?sold_out=${!it.sold_out}`); reloadMenu(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const generateImage = async () => {
    if (!editing?.name) return toast.error("Add a name first");
    setGenBusy(true);
    try {
      const tags = (editing.dietary_tags || []).join(", ");
      const prompt = `${editing.name} (${editing.category}${tags ? ", " + tags : ""}${editing.description ? " — " + editing.description : ""})`;
      const { data } = await api.post("/menu-bulk/generate-image", { prompt });
      setEditing({ ...editing, image_url: data.image_url });
      toast.success("AI image generated");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setGenBusy(false); }
  };

  const onBulkFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/menu-bulk/upload?auto_image=${autoImg ? "true" : "false"}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkResult(data);
      toast.success(`Created ${data.created_count} items${data.skipped ? ` (skipped ${data.skipped})` : ""}`);
      reloadMenu();
    } catch (err) { toast.error(formatErr(err)); }
    finally {
      setBulkBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (user?.role !== "vendor") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="bg-white rounded-3xl p-8 border border-border text-center max-w-md">
          <Store size={32} className="mx-auto text-brand mb-3" />
          <div className="font-heading text-2xl font-bold">Vendor portal</div>
          <p className="text-sm text-ink-600 mt-2">Sign in as a vendor user. Try <span className="font-mono">v_spice@grubpe.com / grubpe123</span></p>
          <Link to="/login" data-testid="vendor-login" className="mt-4 inline-block px-5 py-2.5 rounded-full bg-brand text-white font-semibold">Sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream warm-grain">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-cream/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo size="md" />
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-ink-500 ml-2 px-2 py-0.5 rounded-full bg-cream-200">Vendor</span>
          </Link>
          <div className="hidden md:flex items-center gap-1">
            {["dashboard","orders","menu"].map(t => (
              <button key={t} data-testid={`v-tab-${t}`} onClick={() => setTab(t)} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab===t?'bg-ink text-white':'text-ink-700 hover:bg-cream-200'}`}>{t.toUpperCase()}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold hidden sm:inline">{data?.vendor?.name}</span>
            <button data-testid="vendor-logout" onClick={() => { logout(); nav("/"); }} className="p-2 rounded-full hover:bg-cream-200"><LogOut size={18} /></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {tab === "dashboard" && (
          <>
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">{data?.vendor?.category}</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">{data?.vendor?.name}</h1>
              </div>
              <div className="px-4 py-2 rounded-full bg-sun text-ink font-semibold text-sm">Settlement: {data?.vendor?.settlement_pct}% / platform: {(100 - (data?.vendor?.settlement_pct || 0)).toFixed(0)}%</div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
              <div className="bg-white rounded-xl p-5 border border-border">
                <div className="flex items-center justify-between"><div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Today</div><TrendingUp size={16} className="text-brand" /></div>
                <div className="font-heading text-3xl font-extrabold mt-2">{inr(data?.revenue_today)}</div>
                <div className="text-xs text-ink-500 mt-1">{data?.orders_today} orders</div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-border">
                <div className="flex items-center justify-between"><div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">All-time</div><ShoppingBag size={16} className="text-brand" /></div>
                <div className="font-heading text-3xl font-extrabold mt-2">{inr(data?.revenue_total)}</div>
                <div className="text-xs text-ink-500 mt-1">{data?.orders_total} orders</div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-border">
                <div className="flex items-center justify-between"><div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Pending payout</div><Banknote size={16} className="text-emerald-700" /></div>
                <div className="font-heading text-3xl font-extrabold mt-2 text-emerald-700">{inr(data?.pending_payout)}</div>
                <div className="text-xs text-ink-500 mt-1">awaiting transfer</div>
              </div>
              <div className="bg-ink text-white rounded-xl p-5">
                <div className="flex items-center justify-between"><div className="text-xs font-bold uppercase tracking-[0.2em] text-sun">Menu items</div><ChefHat size={16} className="text-sun" /></div>
                <div className="font-heading text-3xl font-extrabold mt-2">{menu.length}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-white rounded-xl p-6 border border-border">
                <div className="flex items-center gap-2 text-ink-500 text-xs font-bold uppercase tracking-[0.2em]"><Trophy size={14} /> Top items</div>
                <div className="mt-3 space-y-2">
                  {data?.top_items?.length ? data.top_items.map((t,i) => (
                    <div key={t.name} className="flex items-center justify-between p-3 bg-cream-100 rounded-lg">
                      <div className="flex items-center gap-3"><div className="w-7 h-7 rounded-md bg-ink text-white text-xs font-bold flex items-center justify-center">{i+1}</div><span className="font-semibold">{t.name}</span></div>
                      <span className="text-sm font-bold text-brand">{t.count}×</span>
                    </div>
                  )) : <div className="text-sm text-ink-500">No data yet</div>}
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 border border-border">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Recent payouts</div>
                <div className="mt-3 space-y-2">
                  {data?.payouts?.slice(0,5).map(p => (
                    <div key={p.id} className="p-3 bg-cream-100 rounded-lg flex items-center justify-between">
                      <div>
                        <div className="text-xs text-ink-500">{p.period_start} → {p.period_end}</div>
                        <div className="font-semibold">{inr(p.net)}</div>
                      </div>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${p.status==='paid'?'bg-emerald-100 text-emerald-900':'bg-amber-100 text-amber-900'}`}>{p.status}</span>
                    </div>
                  ))}
                  {!data?.payouts?.length && <div className="text-sm text-ink-500">No payouts yet</div>}
                </div>
              </div>
            </div>
          </>
        )}

        {tab === "orders" && (
          <div className="bg-white rounded-xl border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream-200">
                <tr>
                  <th className="p-3 text-left text-xs uppercase font-bold">Time</th>
                  <th className="p-3 text-left text-xs uppercase font-bold">Type</th>
                  <th className="p-3 text-left text-xs uppercase font-bold">Scheduled</th>
                  <th className="p-3 text-left text-xs uppercase font-bold">Items</th>
                  <th className="p-3 text-right text-xs uppercase font-bold">Total</th>
                  <th className="p-3 text-left text-xs uppercase font-bold">Status</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => {
                  const today = new Date().toISOString().slice(0,10);
                  const isPreorder = (o.type === "preorder" || o.type === "special") && (o.scheduled_date || "") > today;
                  return (
                  <tr key={o.id} className={`border-t border-border ${isPreorder ? 'bg-emerald-50' : ''}`} data-testid={`vendor-order-${o.id}`}>
                    <td className="p-3 text-xs text-ink-600">{new Date(o.created_at).toLocaleString()}</td>
                    <td className="p-3">
                      {isPreorder
                        ? <span data-testid={`vendor-preorder-${o.id}`} className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-200 text-emerald-900">Preorder</span>
                        : <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-cream-300 text-ink-700">{o.type || 'live'}</span>}
                    </td>
                    <td className="p-3 text-xs text-ink-600">{o.scheduled_date || '—'}{o.meal_slot ? ` · ${o.meal_slot}` : ''}</td>
                    <td className="p-3 max-w-md truncate">{o.items.map(i => `${i.qty}× ${i.name}`).join(", ")}</td>
                    <td className="p-3 text-right font-bold">{inr(o.total)}</td>
                    <td className="p-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_COLOR[o.status] || 'bg-cream-300'}`}>{o.status}</span></td>
                    <td className="p-3 text-right">
                      {o.status === "pending" && <button data-testid={`v-prep-${o.id}`} onClick={() => update(o.id, "preparing")} className="px-2.5 py-1 rounded-md text-xs font-bold bg-blue-100 text-blue-900">Prep</button>}
                      {o.status === "preparing" && <button data-testid={`v-ready-${o.id}`} onClick={() => update(o.id, "ready")} className="px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-100 text-emerald-900">Ready</button>}
                      {o.status === "ready" && <button data-testid={`v-done-${o.id}`} onClick={() => update(o.id, "completed")} className="px-2.5 py-1 rounded-md text-xs font-bold bg-ink text-white">Done</button>}
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        )}

        {tab === "menu" && (
          <>
            <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Your menu</div>
                <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-ink mt-1">Items at {data?.vendor?.name}</h2>
                <div className="text-sm text-ink-600 mt-1">Add, edit, mark sold-out — or upload a CSV with one row per item.</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button data-testid="v-bulk-template" onClick={async () => {
                  try {
                    const { data } = await api.get("/menu-bulk/template.csv", { responseType: "blob" });
                    const url = URL.createObjectURL(data);
                    const a = document.createElement("a"); a.href = url; a.download = "grubpe-menu-template.csv"; a.click();
                    URL.revokeObjectURL(url);
                  } catch (err) { toast.error(formatErr(err)); }
                }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cream-200 text-ink-700 text-xs font-bold hover:bg-cream-300">
                  <Download size={14} /> CSV template
                </button>
                <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cream-200 cursor-pointer">
                  <input data-testid="v-bulk-auto-image" type="checkbox" checked={autoImg} onChange={(e)=>setAutoImg(e.target.checked)} className="accent-brand"/>
                  <span className="text-xs font-bold inline-flex items-center gap-1"><Sparkles size={12}/> AI images</span>
                </label>
                <button data-testid="v-bulk-upload-btn" onClick={()=>fileRef.current?.click()} disabled={bulkBusy}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink text-white text-xs font-bold disabled:opacity-60">
                  {bulkBusy ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>} {bulkBusy ? "Uploading…" : "Bulk upload"}
                </button>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onBulkFile} className="hidden" data-testid="v-bulk-file"/>
                <button data-testid="v-new-item" onClick={() => setEditing({ ...empty })}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white font-semibold">
                  <Plus size={16}/> New item
                </button>
              </div>
            </div>

            {bulkResult && (
              <div data-testid="v-bulk-result" className="mb-5 p-4 rounded-2xl bg-emerald-50 border border-emerald-200">
                <div className="text-sm font-bold text-emerald-900">Bulk import: {bulkResult.created_count} created · {bulkResult.skipped} skipped</div>
                {bulkResult.errors?.length > 0 && (
                  <div className="mt-2 text-xs text-amber-900 space-y-0.5">
                    {bulkResult.errors.slice(0, 8).map((er, i) => <div key={i}>Row {er.row}: {er.reason}</div>)}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {menu.map(m => (
                <div key={m.id} className="bg-white rounded-2xl border border-border overflow-hidden" data-testid={`v-item-${m.id}`}>
                  <div className="aspect-[4/3] bg-cream-200 overflow-hidden relative">
                    {m.image_url && <img src={m.image_url} alt={m.name} className="w-full h-full object-cover" />}
                    {m.sold_out && <div className="absolute inset-0 bg-ink/60 flex items-center justify-center"><span className="text-white font-bold">Sold out</span></div>}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between"><div className="font-heading font-bold">{m.name}</div><div className="font-heading font-extrabold">{inr(m.price)}</div></div>
                    <div className="text-[11px] uppercase tracking-wider text-ink-500">{m.category} · GST {m.tax_rate || 5}%</div>
                    <div className="mt-2 flex flex-wrap gap-1">{m.dietary_tags?.map(t => <AllergenBadge key={t} tag={t} />)}</div>
                    <div className="mt-4 flex items-center gap-2">
                      <button data-testid={`v-edit-${m.id}`} onClick={() => setEditing(m)} className="p-2 rounded-lg bg-cream-200 hover:bg-cream-300"><Edit size={14}/></button>
                      <button data-testid={`v-delete-${m.id}`} onClick={() => removeItem(m.id)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-brand"><Trash2 size={14}/></button>
                      <button data-testid={`v-soldout-${m.id}`} onClick={() => toggleSoldOut(m)} className="ml-auto px-3 py-1.5 rounded-lg text-xs font-semibold bg-cream-200 hover:bg-cream-300">{m.sold_out ? "Mark available" : "Mark sold out"}</button>
                    </div>
                  </div>
                </div>
              ))}
              {menu.length === 0 && <div className="col-span-full text-center text-ink-500 py-12 bg-cream-100 rounded-3xl border border-dashed border-border">No items yet — click "New item" or bulk-upload a CSV.</div>}
            </div>

            {editing && (
              <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4 py-8 overflow-y-auto" onClick={() => setEditing(null)}>
                <div className="bg-white rounded-3xl p-6 sm:p-8 w-full max-w-2xl shadow-floating" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between">
                    <div className="font-heading text-2xl font-bold">{editing.id ? "Edit item" : "New item"}</div>
                    <button onClick={() => setEditing(null)} className="text-ink-500"><X size={20}/></button>
                  </div>
                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Name" value={editing.name} onChange={v => setEditing({...editing, name: v})} testid="v-form-name" />
                    <Field label="Category" value={editing.category} onChange={v => setEditing({...editing, category: v})} testid="v-form-cat" />
                    <Field label="Selling price ₹" type="number" value={editing.price} onChange={v => setEditing({...editing, price: v})} testid="v-form-price" />
                    <Field label="Cost ₹" type="number" value={editing.cost_price} onChange={v => setEditing({...editing, cost_price: v})} testid="v-form-cost" />
                    <Field label="GST %" type="number" value={editing.tax_rate} onChange={v => setEditing({...editing, tax_rate: v})} testid="v-form-tax" />
                    <Field label="Prep mins" type="number" value={editing.prep_minutes} onChange={v => setEditing({...editing, prep_minutes: v})} testid="v-form-prep" />

                    <div className="sm:col-span-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-ink-500 flex items-center justify-between">
                        <span>Image</span>
                        <button type="button" data-testid="v-form-img-ai" onClick={generateImage} disabled={genBusy}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-ink text-white text-[11px] font-bold disabled:opacity-60">
                          {genBusy ? <Loader2 size={11} className="animate-spin"/> : <Sparkles size={11}/>}
                          {genBusy ? "Generating…" : "Generate with AI"}
                        </button>
                      </label>
                      {editing.image_url && (
                        <div className="mt-2 aspect-[4/3] w-full rounded-xl overflow-hidden bg-cream-200 border border-border">
                          <img src={editing.image_url} alt="preview" className="w-full h-full object-cover"/>
                        </div>
                      )}
                      <input data-testid="v-form-img" type="text" placeholder="Paste an image URL or click Generate with AI"
                        value={editing.image_url ?? ""} onChange={(e)=>setEditing({...editing, image_url: e.target.value})}
                        className="mt-2 w-full rounded-xl border border-border bg-white px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Description</label>
                      <textarea data-testid="v-form-desc" value={editing.description ?? ""} onChange={e => setEditing({...editing, description: e.target.value})} rows={2}
                        className="mt-1 w-full rounded-xl border border-border bg-white px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Dietary tags</label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {TAGS.map(t => {
                          const active = editing.dietary_tags?.includes(t);
                          return (
                            <button key={t} data-testid={`v-tag-${t}`}
                              onClick={() => setEditing({ ...editing, dietary_tags: active ? editing.dietary_tags.filter(x => x !== t) : [...(editing.dietary_tags || []), t] })}
                              className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${active ? "bg-ink text-white border-ink" : "bg-white text-ink-700 border-border"}`}>{t}</button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-2">
                    <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full text-ink-700 font-semibold hover:bg-cream-200">Cancel</button>
                    <button data-testid="v-form-save" onClick={saveItem} className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold">Save</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", testid, hint }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-wider text-ink-500">{label}</label>
    <input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full rounded-xl border border-border bg-white px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
    {hint && <div className="text-[11px] text-ink-500 mt-1">{hint}</div>}
  </div>
);
